
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, Menu, X, Settings } from 'lucide-react';

interface HeaderProps {
  cartCount: number;
  onCartClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ cartCount, onCartClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Prevent stale mobile menu state when route/query changes.
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'BRUMES', path: '/shop?category=Brumes Parfumées' },
    { name: 'SAVONS', path: '/shop?category=Lave-mains' },
    { name: 'CRÈMES', path: '/shop?category=Crèmes Mains' },
    { name: 'GELS', path: '/shop?category=Gels Antiseptiques' },
    { name: 'ROOM SPRAYS', path: '/shop?category=Room Sprays' },

  ];

  const isHome = location.pathname === '/';
  
  // Effet d'immersion : transparent sur la home au top, sinon blanc/crème
  const bgColorClass = (isScrolled || !isHome) 
    ? 'bg-brand-offwhite/95 md:backdrop-blur-md py-3 shadow-sm' 
    : 'bg-transparent py-6';
    
  const textColorClass = (!isScrolled && isHome) ? 'text-white' : 'text-brand-black';
  const LOGO_URL = "https://fbaptclwziljpmozftiz.supabase.co/storage/v1/object/public/images/logo.jpeg";

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-700 ${bgColorClass}`}>
      <div className="container mx-auto px-6 grid grid-cols-3 items-center">
        
        <nav className="hidden md:flex items-center space-x-8">
          {navLinks.slice(0, 2).map((link) => (
            <Link 
              key={link.name} 
              to={link.path}
              className={`text-[8px] uppercase tracking-[0.4em] font-bold hover:text-brand-gold luxury-transition ${textColorClass}`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        <div className="md:hidden flex items-center">
          <button className={`${textColorClass}`} onClick={() => setIsMobileMenuOpen(true)}>
            <Menu size={20} />
          </button>
        </div>

        <div className="flex justify-center">
          <Link to="/" className="group">
            <div className={`luxury-transition rounded-full overflow-hidden border-2 group-hover:border-brand-gold/20 ${isScrolled || !isHome ? 'w-12 border-transparent' : 'w-20 border-white/20'}`}>
              <img src={LOGO_URL} alt="R & C Essentials" className="w-full h-full object-contain" />
            </div>
          </Link>
        </div>

        <div className="flex items-center justify-end space-x-6">
          <nav className="hidden md:flex items-center space-x-8 mr-6">
            {navLinks.slice(2).map((link) => (
              <Link 
                key={link.name} 
                to={link.path}
                className={`text-[8px] uppercase tracking-[0.4em] font-bold hover:text-brand-gold luxury-transition ${textColorClass}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className={`p-2 rounded-full hover:bg-brand-gold/20 luxury-transition ${textColorClass}`}
            >
              <Settings size={18} className="animate-[spin_20s_linear_infinite]" />
            </button>

            <button onClick={onCartClick} className={`relative group p-1 ${textColorClass}`}>
              <ShoppingBag size={18} className="group-hover:text-brand-gold transition-colors" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-gold text-white text-[7px] w-3.5 h-3.5 flex items-center justify-center rounded-full font-bold shadow-md">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="fixed top-0 left-0 w-screen h-[100dvh] bg-brand-offwhite/95 backdrop-blur-sm z-[120] flex flex-col p-8 animate-in fade-in duration-300 overflow-y-auto">
          <div className="flex justify-between items-center mb-16">
            <img src={LOGO_URL} alt="Logo" className="w-16 h-auto rounded-full" />
            <button onClick={() => setIsMobileMenuOpen(false)}>
              <X size={24} className="text-brand-black" />
            </button>
          </div>
          <nav className="flex flex-col space-y-10 items-center pb-10">
            {navLinks.map((link) => (
              <Link 
                key={link.name} 
                to={link.path} 
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-serif text-3xl text-brand-black hover:text-brand-gold"
              >
                {link.name}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
